import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box, Typography, Stack, Chip, InputBase, Skeleton, Button, IconButton, Tooltip, Dialog, DialogContent,
    DialogActions, TextField, Alert, Snackbar, CircularProgress, FormControlLabel, Checkbox, MenuItem, Switch, Popover,
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EventRepeatRoundedIcon from '@mui/icons-material/EventRepeatRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { useSelector } from 'react-redux';
import http, { apiErrorMessage } from '../../Api/http';
import {
    GetEmployees, GetGrowthIncrements, AddGrowthIncrement, UpdateGrowthIncrement, DeleteGrowthIncrement,
    GetRegularIncrements, SaveRegularIncrements, salaryStructureDashboard, updateSalaryStructureByEmployeeCode,
} from '../../Api/Api';
import { selectUserTypeId, selectEffectiveModules, USER_TYPE } from '../../redux/slices/authSlice';
import { hasModule } from '../../data/serverModules';
import { fmtApiDate, toApiDate, toInputDate } from '../../utils/apiFields';
import { inr, cleanName, parseApiDate } from '../../utils/format';
import { passphraseConfig, setSalaryPassphrase, isPassphraseError, passphraseErrorText } from '../../utils/salaryPassphrase';
import { PRIMARY, PRIMARY_LIGHT } from '../../theme';
import SearchSelect from '../SearchSelect';
import SecretDialog from '../SecretDialog';
import useOrgOptions from '../../hooks/useOrgOptions';
import { figures } from '../LeaveAttendanceComps/PayrollComps/SalaryStructures';
import { card, field, solidBtn, ghostBtn, tonalBtn, EmptyState, LoadAlert, ConfirmDialog } from '../uiKit';

// Regular: the yearly increment percentage used until an admin saves one. An admin can give any one person their own
// percentage or a fixed amount, and can apply an increment straight onto that person's salary structure.
export const REGULAR_INCREMENT_PERCENT = 5;

const pick = (r, ...keys) => keys.map((k) => r?.[k]).find((v) => v !== undefined && v !== null && v !== '');
const isActive = (p) => String(p.status || '').toLowerCase() === 'active';

// "Newly added": onboarded in the last 30 days (or not joined yet). Everyone else shows in the Regular table by default;
// new people only get there when an admin adds them with "Add employee".
const NEW_DAYS = 30;
const isNewEmployee = (p) => {
    const d = parseApiDate(p.createdOn) || parseApiDate(p.dateOfJoining);
    return Boolean(d) && (Date.now() - d.getTime()) / 86400000 <= NEW_DAYS;
};

// The next anniversary of the joining date, from today (none before the first one).
const nextDue = (joined) => {
    const j = parseApiDate(joined);
    if (!j) return null;
    const now = new Date();
    const d = new Date(now.getFullYear(), j.getMonth(), j.getDate());
    if (d <= now) d.setFullYear(d.getFullYear() + 1);
    return d.getFullYear() === j.getFullYear() ? null : d;
};
const fmtLong = (d) => d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

// What a salary structure becomes when its basic salary is raised by `amount` (HRA / DA follow the basic).
// `type` says what the amount is added to: the basic salary, or the gross (then the basic is back-worked so that
// HRA / DA / allowances still add up to the new gross).
const raised = (s, amount, type = 'basic') => {
    const f = figures(s);
    let basic = f.basic + amount;
    if (type === 'gross') {
        const target = f.gross + amount;
        basic = Math.round((target - f.conveyance - f.special) / (1 + (Number(f.hraP) + Number(f.daP)) / 100));
    }
    const gross = figures({ ...s, basicSalary: basic, totalEarnings: undefined }).gross;
    return { f, basic, gross };
};
const SALARY_TYPES = [['basic', 'Basic salary'], ['gross', 'Gross salary']];
const REASON_TYPES = ['Annual review', 'Performance', 'Promotion', 'Market correction', 'Other'];
const EXTRA_BLANK = { reasonType: '', newDesignation: '', approvedBy: '', nextReview: '', current: '' };

// Dates here are picked by month. They are kept as the first day of that month (yyyy-mm-01) so the rest of the
// code and the server still get a full date.
const localToday = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const thisMonthStart = () => `${localToday().slice(0, 7)}-01`;
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
// One field for month and year: it shows e.g. "October 2026" and opens a small panel with year arrows and a month grid.
function MonthField({ label, value, onChange, error }) {
    const [anchor, setAnchor] = useState(null);
    const now = new Date();
    const y = Number((value || '').slice(0, 4)) || 0;
    const m = Number((value || '').slice(5, 7)) || 0;
    const [viewYear, setViewYear] = useState(y || now.getFullYear());
    useEffect(() => { if (anchor) setViewYear(y || now.getFullYear()); }, [anchor]); // eslint-disable-line react-hooks/exhaustive-deps
    const pickMonth = (i) => { onChange(`${viewYear}-${String(i + 1).padStart(2, '0')}-01`); setAnchor(null); };
    const arrow = { color: '#64748B', '&:hover': { bgcolor: PRIMARY_LIGHT, color: PRIMARY } };
    return (
        <>
            <TextField
                label={label} size="small" fullWidth error={error} value={m && y ? `${MONTH_NAMES[m - 1]} ${y}` : ''} placeholder="Select month"
                onClick={(e) => setAnchor(e.currentTarget)} sx={field}
                slotProps={{
                    inputLabel: { shrink: true },
                    input: { readOnly: true, sx: { cursor: 'pointer' }, endAdornment: <CalendarMonthRoundedIcon sx={{ fontSize: 19, color: '#64748B' }} /> },
                    htmlInput: { style: { cursor: 'pointer' } },
                }}
            />
            <Popover
                open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                slotProps={{ paper: { sx: { mt: 0.5, p: 1.5, width: 270, borderRadius: '12px', border: '1px solid #E6EAF1' } } }}
            >
                <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <IconButton size="small" onClick={() => setViewYear((v) => v - 1)} sx={arrow}><ChevronLeftRoundedIcon sx={{ fontSize: 20 }} /></IconButton>
                    <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0F172A' }}>{viewYear}</Typography>
                    <IconButton size="small" onClick={() => setViewYear((v) => v + 1)} sx={arrow}><ChevronRightRoundedIcon sx={{ fontSize: 20 }} /></IconButton>
                </Stack>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.8 }}>
                    {MONTH_NAMES.map((n, i) => {
                        const on = m === i + 1 && y === viewYear;
                        return (
                            <Box key={n} component="button" type="button" onClick={() => pickMonth(i)}
                                sx={{ border: 'none', cursor: 'pointer', height: 36, borderRadius: '8px', fontSize: 13, fontWeight: 700, fontFamily: 'inherit',
                                    bgcolor: on ? PRIMARY : '#F8FAFC', color: on ? '#fff' : '#334155', '&:hover': { bgcolor: on ? PRIMARY : PRIMARY_LIGHT } }}>
                                {n.slice(0, 3)}
                            </Box>
                        );
                    })}
                </Box>
                <Stack direction="row" sx={{ justifyContent: 'space-between', mt: 1.2 }}>
                    <Button size="small" onClick={() => { onChange(''); setAnchor(null); }} sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12.5, color: '#64748B', minWidth: 0 }}>Clear</Button>
                    <Button size="small" onClick={() => { onChange(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`); setAnchor(null); }} sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12.5, color: PRIMARY, minWidth: 0 }}>This month</Button>
                </Stack>
            </Popover>
        </>
    );
}
// Today's date by default, with a date picker to change it.
const CurrentDateField = ({ value, onChange }) => (
    <TextField label="Current date" type="date" size="small" fullWidth value={value || localToday()} onChange={(e) => onChange(e.target.value)}
        // Clicking anywhere in the box, not only the calendar icon, opens the picker.
        slotProps={{ inputLabel: { shrink: true }, htmlInput: { onClick: (e) => { try { e.currentTarget.showPicker(); } catch { /* needs a user gesture; focus is enough */ } }, style: { cursor: 'pointer' } } }} sx={field} />
);

// Details kept with every increment, Regular or Growth: why, any change of designation, who approved it, and when to review next.
function ExtraFields({ v, onChange, designations }) {
    const f = (k) => (e) => onChange(k, e.target.value);
    // Order matters: the dialogs lay fields out two to a row, so these four follow Effective date and pair up
    // as Next review | Reason type, New designation | Approved by.
    return (
        <>
            <MonthField label="Next review month (optional)" value={v.nextReview} onChange={(val) => onChange('nextReview', val)} />
            <TextField select label="Reason type" size="small" fullWidth value={v.reasonType} onChange={f('reasonType')} sx={field}
                slotProps={{ inputLabel: { shrink: true }, select: { displayEmpty: true, renderValue: (x) => x || <span style={{ color: '#94A3B8' }}>Select</span> } }}>
                <MenuItem value="" sx={{ fontSize: 13.5 }}><em>—</em></MenuItem>
                {REASON_TYPES.map((r) => <MenuItem key={r} value={r} sx={{ fontSize: 13.5 }}>{r}</MenuItem>)}
            </TextField>
            <SearchSelect
                label="New designation (if promoted)" value={v.newDesignation} onChange={f('newDesignation')} searchPlaceholder="Search designations…" sx={field}
                options={[{ value: '', label: 'No change', muted: true }, ...designations.map((d) => ({ value: d, label: d }))]}
            />
            <TextField label="Approved by (optional)" size="small" fullWidth value={v.approvedBy} onChange={f('approvedBy')} sx={field} />
        </>
    );
}

// Two fields to a row; `full` children (notes, toggles, previews) take the whole row.
const FORM_GRID = { display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 2.5, rowGap: 3, alignItems: 'start' };
const Full = ({ children }) => (children ? <Box sx={{ gridColumn: '1 / -1' }}>{children}</Box> : null);
const typeLabel = (t) => (t === 'gross' ? 'gross' : 'basic');

const Th = ({ children }) => <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#6E6B99', letterSpacing: 0.6 }}>{children}</Typography>;
const EMPTY_FORM = { id: null, employeeCode: '', date: '', amount: '', reason: '', alsoRaise: true, salaryType: 'basic', mode: 'amount', percent: '', ...EXTRA_BLANK };

// Two-way switch used on the table and inside both dialogs.
function Switch2({ value, onChange, options, size = 32 }) {
    return (
        <Stack direction="row" sx={{ bgcolor: '#F4F3FB', borderRadius: '7px', p: '3px', width: 'fit-content' }}>
            {options.map(([k, label]) => (
                <Box key={k} component="button" type="button" onClick={() => onChange(k)}
                    sx={{ border: 'none', cursor: 'pointer', px: 2, height: size, borderRadius: '7px', fontSize: 12.5, fontWeight: 700, fontFamily: 'inherit',
                        bgcolor: value === k ? PRIMARY : 'transparent', color: value === k ? '#fff' : '#64748B', '&:hover': { bgcolor: value === k ? PRIMARY : PRIMARY_LIGHT } }}>
                    {label}
                </Box>
            ))}
        </Stack>
    );
}

export default function IncrementsTab() {
    const userType = useSelector(selectUserTypeId);
    const isEmployee = userType === USER_TYPE.EMPLOYEE;
    const canEdit = userType === USER_TYPE.MASTER_ADMIN || userType === USER_TYPE.ADMIN;
    const loginId = useSelector((s) => String(s.auth.user?.loginId || '').toUpperCase());

    // Each kind has its own access module, so a role can be given Regular, Growth or both.
    const modules = useSelector(selectEffectiveModules);
    const kinds = [['regular', 'Regular', 'increments'], ['growth', 'Growth', 'increments-growth']].filter(([, , m]) => hasModule(modules, m));
    const [pickedKind, setKind] = useState('regular');
    const kind = kinds.some(([k]) => k === pickedKind) ? pickedKind : (kinds[0]?.[0] || 'regular');
    const has = (k) => kinds.some(([x]) => x === k);

    const [people, setPeople] = useState([]);
    const [growth, setGrowth] = useState([]);
    // Regular settings: the yearly percentage and per-person overrides ({ [employeeCode]: { percent?, amount? } }).
    const [regPercent, setRegPercent] = useState(REGULAR_INCREMENT_PERCENT);
    const [overrides, setOverrides] = useState({});
    // Salary structures by employee code, and whether the server is hiding the amounts behind the Encryption Password.
    const [structures, setStructures] = useState({});
    const [salaryLocked, setSalaryLocked] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [q, setQ] = useState('');
    const [saving, setSaving] = useState(false);
    const [snack, setSnack] = useState(null);

    const [ovRow, setOvRow] = useState(null);
    const [ov, setOv] = useState({ mode: 'percent', value: '', date: '', reason: '', salaryType: 'basic', ...EXTRA_BLANK });
    const { designationNames } = useOrgOptions({ quiet: isEmployee });
    const [applyAsk, setApplyAsk] = useState(false);
    const [dialog, setDialog] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [tried, setTried] = useState(false);
    const [formError, setFormError] = useState('');
    const [confirm, setConfirm] = useState(null);
    const [unlockOpen, setUnlockOpen] = useState(false);
    const [unlockBusy, setUnlockBusy] = useState(false);
    const [unlockError, setUnlockError] = useState('');

    const readStructures = (res) => {
        const d = res?.data && !res.data.error ? res.data.data || {} : null;
        if (!d) return null;
        const list = Array.isArray(d.salaryStructures) ? d.salaryStructures : [];
        return { locked: !!d.amountsLocked, map: Object.fromEntries(list.map((s) => [s.employeeCode ?? s.rollNumber, s])) };
    };

    // An employee may not be allowed some of these lists; a denied one must not bounce them to the dashboard.
    const quiet = useMemo(() => (isEmployee ? { noModuleBounce: true } : {}), [isEmployee]);
    const load = useCallback(async () => {
        setLoading(true); setLoadError('');
        try {
            const [emp, gr, reg, st] = await Promise.allSettled([
                http.get(GetEmployees, { params: { search: '', page: 1, pageSize: 1000 }, ...quiet }),
                http.get(GetGrowthIncrements, quiet),
                http.get(GetRegularIncrements, quiet),
                http.get(salaryStructureDashboard, { ...passphraseConfig(), ...quiet }),
            ]);
            const rd = reg.status === 'fulfilled' && !reg.value.data?.error ? reg.value.data?.data : null;
            if (rd && rd.percent !== null && rd.percent !== '' && Number(rd.percent) >= 0) setRegPercent(Number(rd.percent));
            if (rd && Array.isArray(rd.overrides)) {
                setOverrides(Object.fromEntries(rd.overrides.filter((o) => o?.employeeCode).map((o) => [o.employeeCode, {
                    percent: o.percent != null && o.percent !== '' ? Number(o.percent) : undefined,
                    amount: o.amount != null && o.amount !== '' ? Number(o.amount) : undefined,
                    date: o.effectiveDate ? toInputDate(o.effectiveDate) : '', reason: o.reason || '',
                    salaryType: o.salaryType === 'gross' ? 'gross' : 'basic',
                    reasonType: o.reasonType || '', newDesignation: o.newDesignation || '', approvedBy: o.approvedBy || '',
                    nextReview: o.nextReview ? toInputDate(o.nextReview) : '',
                }])));
            }
            const s = st.status === 'fulfilled' ? readStructures(st.value) : null;
            setStructures(s?.map || {});
            setSalaryLocked(s ? s.locked : false);

            if (emp.status === 'fulfilled') {
                const b = emp.value.data;
                const none = b?.error && /not found|no employee|no record|no data/i.test(b.message || '');
                if (b?.error && !none) setLoadError(b.message || 'Could not load employees.');
                setPeople(b?.data?.items || []);
            } else if (emp.reason?.response?.status !== 404) {
                setLoadError(emp.reason?.response || emp.reason?.request ? apiErrorMessage(emp.reason, 'Could not load employees.') : emp.reason.message);
            }
            // A missing list (or an endpoint the server doesn't have yet) is just "nothing recorded".
            const g = gr.status === 'fulfilled' && !gr.value.data?.error ? gr.value.data?.data : null;
            setGrowth(Array.isArray(g) ? g : (g?.items || g?.increments || []));
        } finally {
            setLoading(false);
        }
    }, [quiet]);
    useEffect(() => { load(); }, [load]);

    const needle = q.trim().toLowerCase();
    const mine = useCallback((code) => !isEmployee || String(code || '').toUpperCase() === loginId, [isEmployee, loginId]);

    // Every active person with their increment worked out; `regularAll` below is only the ones added to Regular.
    const regularEveryone = useMemo(() => people
        .filter((p) => isActive(p) && mine(p.employeeCode))
        .map((p) => {
            const s = structures[p.employeeCode];
            const fig = s && !s.amountsLocked && !salaryLocked ? figures(s) : null;
            const basic = fig ? fig.basic : null;
            const o = overrides[p.employeeCode] || {};
            const type = o.salaryType === 'gross' ? 'gross' : 'basic';
            // The percentage is taken on the chosen salary: basic or gross.
            const base = type === 'gross' ? (fig ? fig.gross : null) : basic;
            // A fixed amount wins, then the person's own percentage, then the yearly percentage.
            let amount = null;
            let pct = o.percent ?? regPercent;
            if (o.amount != null) { amount = o.amount; pct = base ? (o.amount / base) * 100 : null; }
            else if (base != null) amount = Math.round((base * pct) / 100);
            const after = fig && amount != null ? raised(s, amount, type) : null;
            return {
                key: p.id, name: cleanName(p.fullName), code: p.employeeCode || '', joined: p.dateOfJoining, s, type, isNew: isNewEmployee(p),
                basic, gross: fig ? fig.gross : null, amount, pct, custom: o.amount != null || o.percent != null, fixedAmount: o.amount != null,
                newBasic: after ? after.basic : null, newGross: after ? after.gross : null, due: nextDue(p.dateOfJoining),
            };
        })
        .sort((a, b) => a.name.localeCompare(b.name)), [people, structures, salaryLocked, overrides, regPercent, mine]);
    const regularAll = useMemo(() => regularEveryone.filter((r) => overrides[r.code] || !r.isNew), [regularEveryone, overrides]);
    // New employees not added to Regular yet: the only people the Add employee list offers.
    const addableCodes = useMemo(() => new Set(regularEveryone.filter((r) => r.isNew && !overrides[r.code]).map((r) => r.code)), [regularEveryone, overrides]);
    const regularRows = useMemo(() => regularAll.filter((r) => !needle || `${r.name} ${r.code}`.toLowerCase().includes(needle)), [regularAll, needle]);

    const growthAll = useMemo(() => growth
        .filter((r) => mine(pick(r, 'employeeCode', 'code')))
        .map((r) => ({
            id: r.id,
            code: pick(r, 'employeeCode', 'code') || '',
            name: cleanName(pick(r, 'employeeName', 'name') || people.find((p) => p.employeeCode === pick(r, 'employeeCode', 'code'))?.fullName || ''),
            date: pick(r, 'effectiveDate', 'date'),
            amount: Number(pick(r, 'amount', 'incrementAmount')) || 0,
            reason: pick(r, 'reason', 'remarks', 'notes') || '',
            salaryType: r.salaryType === 'gross' ? 'gross' : 'basic',
            reasonType: r.reasonType || '', newDesignation: r.newDesignation || '', approvedBy: r.approvedBy || '',
            nextReview: r.nextReview ? toInputDate(r.nextReview) : '',
        }))
        .sort((a, b) => String(b.date || '').localeCompare(String(a.date || ''))), [growth, people, mine]);
    const growthRows = useMemo(() => growthAll.filter((r) => !needle || `${r.name} ${r.code} ${r.reason}`.toLowerCase().includes(needle)), [growthAll, needle]);

    const employeeOptions = useMemo(() => people.filter(isActive).map((p) => ({ value: p.employeeCode, label: `${cleanName(p.fullName)} · ${p.employeeCode}` })), [people]);
    const nameOf = (code) => cleanName(people.find((p) => p.employeeCode === code)?.fullName || code);

    const growthOf = (code) => growthAll.filter((g) => g.code === code);
    const regularNote = (code) => {
        const o = overrides[code];
        if (!o || (o.amount == null && o.percent == null)) return '';
        return o.amount != null ? `a fixed ${inr(o.amount)} a year` : `its own ${o.percent}% a year`;
    };

    // ---- the salary structure: apply an increment to the basic salary
    const applyToStructure = async (code, structure, amount, type = 'basic') => {
        const { f, basic, gross } = raised(structure, amount, type);
        await http.put(updateSalaryStructureByEmployeeCode, {
            EmployeeCode: code, BasicSalary: basic, HraPercent: f.hraP, DaPercent: f.daP,
            ConveyanceAllowance: String(f.conveyance), SpecialAllowance: String(f.special), TotalEarnings: gross,
        }, passphraseConfig());
    };
    const refreshStructures = async () => {
        try {
            const s = readStructures(await http.get(salaryStructureDashboard, passphraseConfig()));
            if (s) { setStructures(s.map); setSalaryLocked(s.locked); }
        } catch { /* keep what is shown */ }
    };
    const failMessage = (err, fallback) => {
        if (isPassphraseError(err)) { setUnlockError(passphraseErrorText(err) || ''); setUnlockOpen(true); return 'Unlock salary figures with the Encryption Password, then try again.'; }
        return err?.response || err?.request ? apiErrorMessage(err, fallback) : err.message;
    };

    const submitUnlock = async ({ pass }) => {
        setUnlockBusy(true); setUnlockError('');
        try {
            const s = readStructures(await http.get(salaryStructureDashboard, passphraseConfig(pass)));
            if (!s || s.locked) { setUnlockError('Incorrect password.'); return; }
            setSalaryPassphrase(pass);
            setStructures(s.map); setSalaryLocked(false); setUnlockOpen(false);
        } catch (err) {
            setUnlockError(passphraseErrorText(err) || apiErrorMessage(err, 'Could not verify the password.'));
        } finally { setUnlockBusy(false); }
    };

    // ---- Regular: one person
    const ovAmount = (row) => {
        const n = Number(ov.value);
        if (ov.value === '' || Number.isNaN(n) || n < 0) return null;
        if (ov.mode === 'amount') return n;
        const base = ov.salaryType === 'gross' ? row?.gross : row?.basic;
        return base != null ? Math.round((base * n) / 100) : null;
    };
    const ovFromOverride = (code) => {
        const o = overrides[code] || {};
        const base = {
            date: o.date || '', reason: o.reason || '', salaryType: o.salaryType === 'gross' ? 'gross' : 'basic',
            reasonType: o.reasonType || '', newDesignation: o.newDesignation || '', approvedBy: o.approvedBy || '', nextReview: o.nextReview || '', current: o.current || '',
        };
        return o.amount != null ? { mode: 'amount', value: String(o.amount), ...base } : { mode: 'percent', value: o.percent != null ? String(o.percent) : '', ...base };
    };
    const openOverride = (r) => { setOv(ovFromOverride(r.code)); setApplyAsk(false); setOvRow(r); };
    // "Add employee": the same dialog, with an employee picker on top so a newly onboarded person can be given an increment.
    const openAddRegular = () => {
        setOv({ mode: 'percent', value: '', date: thisMonthStart(), reason: '', salaryType: 'basic', ...EXTRA_BLANK });
        setApplyAsk(false);
        setOvRow({ add: true, code: '', name: '' });
    };
    // "Add employee" lists only newly onboarded people who are not in the Regular table yet, so nobody is added twice.
    const regularPickOptions = employeeOptions
        .filter((o) => addableCodes.has(o.value))
        .map((o) => (growthOf(o.value).length ? { ...o, hint: 'Already has a Growth increment' } : o));
    const growthPickOptions = employeeOptions.map((o) => (regularNote(o.value) ? { ...o, hint: 'Has its own Regular setting' } : o));
    const pickGrowthEmployee = (code) => setForm((f) => ({ ...f, employeeCode: code, alsoRaise: Boolean(structures[code]) }));
    const pickRegularEmployee = (code) => {
        const row = regularEveryone.find((r) => r.code === code);
        setOv((o) => ({ ...ovFromOverride(code), date: ovFromOverride(code).date || o.date || thisMonthStart() }));
        setApplyAsk(false);
        setOvRow({ ...(row || {}), add: true, code, name: row?.name || nameOf(code) });
    };
    const ovNum = Number(ov.value);
    const ovOk = Boolean(ovRow?.code) && ov.value !== '' && ovNum >= 0 && (ov.mode === 'amount' || ovNum <= 100);

    const saveRegular = async (percent, nextOverrides, doneMsg) => {
        setSaving(true);
        try {
            const { data: body } = await http.post(SaveRegularIncrements, {
                percent,
                overrides: Object.entries(nextOverrides)
                    .filter(([, o]) => o.amount != null || o.percent != null)
                    .map(([employeeCode, o]) => ({
                        employeeCode, percent: o.percent ?? null, amount: o.amount ?? null,
                        effectiveDate: o.date ? toApiDate(o.date) : null, reason: o.reason || '', salaryType: o.salaryType || 'basic',
                        reasonType: o.reasonType || '', newDesignation: o.newDesignation || '', approvedBy: o.approvedBy || '',
                        nextReview: o.nextReview ? toApiDate(o.nextReview) : null, currentDate: toApiDate(o.current || localToday()),
                    })),
            });
            if (body?.error) throw new Error(body.message || 'Could not save.');
            setRegPercent(percent); setOverrides(nextOverrides);
            setSnack({ sev: 'success', msg: doneMsg });
            return true;
        } catch (err) {
            if (err?.response?.status === 404) {
                // The server has no endpoint for this yet: keep the change on this screen so the list still works.
                setRegPercent(percent); setOverrides(nextOverrides);
                setSnack({ sev: 'warning', msg: `${doneMsg} Kept on this screen only — the server does not store regular increments yet, so it is lost when the page reloads.` });
                return true;
            }
            setSnack({ sev: 'error', msg: err?.response || err?.request ? apiErrorMessage(err, 'Could not save.') : err.message });
            return false;
        } finally { setSaving(false); }
    };
    const saveOverride = async () => {
        if (!ovOk) return;
        const next = { ...overrides, [ovRow.code]: { ...(ov.mode === 'amount' ? { amount: ovNum } : { percent: ovNum }), date: ov.date, reason: ov.reason.trim(), salaryType: ov.salaryType, reasonType: ov.reasonType, newDesignation: ov.newDesignation, approvedBy: ov.approvedBy.trim(), nextReview: ov.nextReview, current: ov.current } };
        if (await saveRegular(regPercent, next, `${ovRow.name}'s increment saved.`)) setOvRow(null);
    };
    const resetOverride = async () => {
        const next = { ...overrides };
        delete next[ovRow.code];
        if (await saveRegular(regPercent, next, `${ovRow.name} ${ovRow.isNew ? 'was removed from Regular.' : `now follows the yearly ${regPercent}%.`}`)) setOvRow(null);
    };
    const applyOverride = async () => {
        const amount = ovAmount(ovRow);
        if (amount == null || !ovRow.s) return;
        setSaving(true);
        try {
            await applyToStructure(ovRow.code, ovRow.s, amount, ov.salaryType);
            await refreshStructures();
            const after = raised(ovRow.s, amount, ov.salaryType);
            setSnack({ sev: 'success', msg: `${ovRow.name}'s basic is now ${inr(after.basic)} and gross ${inr(after.gross)}.` });
            setOvRow(null);
        } catch (err) {
            setSnack({ sev: 'error', msg: failMessage(err, 'Could not update the salary structure.') });
        } finally { setSaving(false); setApplyAsk(false); }
    };

    // ---- Growth
    const openAdd = (code = '') => {
        const s = structures[code];
        setForm({ ...EMPTY_FORM, employeeCode: code, date: thisMonthStart(), alsoRaise: Boolean(s) });
        setTried(false); setFormError(''); setDialog('add');
    };
    const openEdit = (r) => { setForm({ id: r.id, employeeCode: r.code, date: toInputDate(r.date), amount: String(r.amount || ''), mode: 'amount', percent: '', reason: r.reason, alsoRaise: false, salaryType: r.salaryType || 'basic', reasonType: r.reasonType, newDesignation: r.newDesignation, approvedBy: r.approvedBy, nextReview: r.nextReview }); setTried(false); setFormError(''); setDialog('edit'); };
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const formStructure = structures[form.employeeCode];
    const canRaise = Boolean(formStructure) && !salaryLocked && !formStructure?.amountsLocked;
    // The amount to give: typed directly, or a percentage of the chosen salary (needs the salary structure).
    const formFig = canRaise ? figures(formStructure) : null;
    const formBase = formFig ? (form.salaryType === 'gross' ? formFig.gross : formFig.basic) : null;
    const pctNum = Number(form.percent);
    const amountNow = form.mode === 'percent'
        ? (formBase != null && form.percent !== '' && pctNum > 0 && pctNum <= 100 ? Math.round((formBase * pctNum) / 100) : 0)
        : (Number(form.amount) || 0);
    const valid = form.employeeCode && form.date && amountNow > 0;

    // Switching kind from inside a dialog hands the same person to the other dialog.
    const switchToGrowth = () => { const code = ovRow.code; setOvRow(null); openAdd(code); };
    const switchToRegular = () => {
        const row = regularEveryone.find((r) => r.code === form.employeeCode);
        if (!row) return;
        setDialog(null);
        if (!addableCodes.has(row.code)) openOverride(row);
        else { pickRegularEmployee(row.code); } // not added to Regular yet: open the Add employee form for them
    };

    const save = async () => {
        setTried(true);
        if (!valid) return;
        setSaving(true); setFormError('');
        const amount = amountNow;
        const raiseNow = dialog === 'add' && form.alsoRaise && canRaise;
        try {
            if (raiseNow) await applyToStructure(form.employeeCode, formStructure, amount, form.salaryType);
        } catch (err) {
            setFormError(failMessage(err, 'Could not update the salary structure.'));
            setSaving(false);
            return;
        }
        try {
            const payload = { employeeCode: form.employeeCode, effectiveDate: toApiDate(form.date), amount, reason: form.reason.trim(), salaryType: form.salaryType,
                calculateBy: form.mode, percent: form.mode === 'percent' ? pctNum : null,
                reasonType: form.reasonType, newDesignation: form.newDesignation, approvedBy: form.approvedBy.trim(),
                nextReview: form.nextReview ? toApiDate(form.nextReview) : null, currentDate: toApiDate(form.current || localToday()) };
            const { data: body } = dialog === 'edit'
                ? await http.put(UpdateGrowthIncrement, { id: form.id, ...payload })
                : await http.post(AddGrowthIncrement, payload);
            if (body?.error) throw new Error(body.message || 'Could not save this increment.');
            setDialog(null);
            setSnack({ sev: 'success', msg: dialog === 'edit' ? 'Growth increment updated.' : 'Growth increment added.' });
        } catch (err) {
            const why = err?.response?.status === 404 ? 'the server does not support growth increments yet' : (err?.response || err?.request ? apiErrorMessage(err, 'it could not be saved') : err.message);
            if (raiseNow) {
                // The salary already moved, so close the dialog rather than invite a second raise.
                setDialog(null);
                setSnack({ sev: 'warning', msg: `Basic salary raised by ${inr(amount)}, but the increment record was not saved: ${why}.` });
            } else setFormError(`Could not save this increment: ${why}.`);
        } finally {
            setSaving(false);
            await Promise.all([load()]);
        }
    };

    const remove = async () => {
        const r = confirm;
        setSaving(true);
        try {
            const { data: body } = await http.delete(DeleteGrowthIncrement, { params: { id: r.id } });
            if (body?.error) throw new Error(body.message || 'Could not remove this increment.');
            setConfirm(null);
            setSnack({ sev: 'success', msg: 'Growth increment removed.' });
            await load();
        } catch (err) {
            setConfirm(null);
            setSnack({ sev: 'error', msg: err?.response || err?.request ? apiErrorMessage(err, 'Could not remove this increment.') : err.message });
        } finally { setSaving(false); }
    };

    const REG_COLS = `1.5fr 0.9fr 0.9fr 0.9fr 0.8fr 0.9fr 0.9fr 0.9fr 1fr${canEdit ? ' 44px' : ''}`;
    const GROW_COLS = `1.6fr 1fr 1fr 1.8fr${canEdit ? ' 90px' : ''}`;
    const money = (v) => (v != null ? inr(v) : (salaryLocked ? '••••' : '—'));

    const lockedBar = salaryLocked && kind === 'regular' && (
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', px: 2.5, py: 1.1, bgcolor: '#FFF8E6', borderBottom: '1px solid #FCEBD0', flexWrap: 'wrap', gap: 1 }}>
            <LockOutlinedIcon sx={{ fontSize: 18, color: '#B7791F' }} />
            <Typography sx={{ fontSize: 12.5, color: '#92600E', flex: 1 }}>Salary figures are locked, so basic and gross salary are hidden.</Typography>
            {canEdit && <Button onClick={() => { setUnlockError(''); setUnlockOpen(true); }} sx={{ ...tonalBtn, height: 32, px: 1.6, fontSize: 12.5 }}>Unlock</Button>}
        </Stack>
    );

    // The note under the person in either dialog when that person already has the other kind.
    // A compact card: icon tile, bold title, one line of detail, and a button to jump to the other kind.
    const otherKindNote = (info, action) => (info ? (
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', px: 1.6, py: 1.2, borderRadius: '10px', bgcolor: '#F8F7FF', border: '1px solid #E4DEFB' }}>
            <Box sx={{ width: 34, height: 34, borderRadius: '9px', bgcolor: '#fff', border: '1px solid #E4DEFB', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <info.icon sx={{ fontSize: 18, color: PRIMARY }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0F172A', lineHeight: 1.3 }}>{info.title}</Typography>
                <Typography sx={{ fontSize: 12, color: '#64748B', lineHeight: 1.4 }}>{info.detail}</Typography>
            </Box>
            {action && <Button onClick={action.onClick} sx={{ ...tonalBtn, height: 32, px: 1.6, fontSize: 12.5, flexShrink: 0, whiteSpace: 'nowrap' }}>{action.label}</Button>}
        </Stack>
    ) : null);
    const growthInfo = (code) => {
        const g = growthOf(code);
        if (!g.length) return null;
        const top = g[0];
        return {
            icon: TrendingUpRoundedIcon, title: 'Has a Growth increment',
            detail: `+${inr(top.amount)}${top.date ? ` on ${fmtApiDate(top.date)}` : ''}${g.length > 1 ? ` · and ${g.length - 1} more` : ''}`,
        };
    };
    const regularInfo = (code) => {
        const note = regularNote(code);
        return {
            icon: EventRepeatRoundedIcon, title: 'In the Regular structure',
            detail: note ? `Own setting: ${note}` : `Follows the yearly ${regPercent}%. Set up Regular first if it needs changing.`,
        };
    };

    const ovPreviewAmount = ovRow ? ovAmount(ovRow) : null;

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ ...card, overflow: 'hidden' }}>
                <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.8, gap: 1.5, flexWrap: 'wrap', borderBottom: '1px solid #EEF0F6' }}>
                    <Switch2 value={kind} onChange={(k) => { setKind(k); setQ(''); }} options={kinds.map(([k, label]) => [k, label])} />
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', bgcolor: '#fff', border: '1px solid #E6EAF1', borderRadius: '7px', px: 1.4, height: 38, width: { xs: '100%', sm: 240 } }}>
                            <SearchRoundedIcon sx={{ fontSize: 18, color: '#98A0AE' }} />
                            <InputBase placeholder="Search employee…" value={q} onChange={(e) => setQ(e.target.value)} sx={{ fontSize: 13, flex: 1 }} />
                        </Stack>
                        {kind === 'regular' && canEdit && (
                            <Button startIcon={<AddRoundedIcon />} onClick={openAddRegular} sx={{ ...solidBtn, height: 38, px: 1.8, fontSize: 13 }}>Add employee</Button>
                        )}
                        {kind === 'growth' && canEdit && (
                            <Button startIcon={<AddRoundedIcon />} onClick={() => openAdd()} sx={{ ...solidBtn, height: 38, px: 1.8, fontSize: 13 }}>Add growth increment</Button>
                        )}
                    </Stack>
                </Stack>

                {kind === 'growth' && (
                    <Box sx={{ px: 2.5, py: 1.2, bgcolor: '#FBFAFF', borderBottom: '1px solid #EEF0F6' }}>
                        <Typography sx={{ fontSize: 12.5, color: '#64748B' }}>
                            Free-form increments given to a person for any amount. {canEdit ? 'You can add, change or remove them.' : 'View only.'}
                        </Typography>
                    </Box>
                )}
                {lockedBar}

                {loading ? (
                    <Box sx={{ p: 2.5 }}>{[0, 1, 2, 3].map((i) => <Skeleton key={i} variant="text" height={34} />)}</Box>
                ) : loadError ? (
                    <Box sx={{ p: 3 }}>
                        <LoadAlert sx={{ borderRadius: '9px' }} action={<Button size="small" onClick={load} sx={{ textTransform: 'none', fontWeight: 700 }}>Retry</Button>}>{loadError}</LoadAlert>
                    </Box>
                ) : kind === 'regular' ? (
                    regularRows.length === 0 ? (
                        <EmptyState icon={TrendingUpRoundedIcon} title={q ? 'Nothing matches that search' : 'No one in Regular yet'} hint={canEdit ? 'Employees onboarded earlier show here on their own. Use Add employee for anyone newly onboarded.' : 'You will appear here once you are added to the yearly increment.'} />
                    ) : (
                        <Box sx={{ overflowX: 'auto' }}>
                            <Box sx={{ minWidth: 1120 }}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: REG_COLS, gap: 2, px: 2.5, py: 1.2, bgcolor: '#FAFAFD', borderBottom: '1px solid #E6EAF1' }}>
                                    {['EMPLOYEE', 'JOINED', 'BASIC', 'GROSS', 'YEARLY %', 'INCREMENT', 'NEW BASIC', 'NEW GROSS', 'NEXT DUE', ...(canEdit ? [''] : [])].map((h, i) => <Th key={`${h}${i}`}>{h}</Th>)}
                                </Box>
                                {regularRows.map((r) => (
                                    <Box
                                        key={r.key} onClick={canEdit ? () => openOverride(r) : undefined}
                                        sx={{ display: 'grid', gridTemplateColumns: REG_COLS, gap: 2, px: 2.5, py: 1.4, alignItems: 'center', borderBottom: '1px solid #F1F2F7', ...(canEdit && { cursor: 'pointer', '&:hover': { bgcolor: '#FAFAFD' } }) }}
                                    >
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#0F172A' }} noWrap>{r.name || '—'}</Typography>
                                            <Typography sx={{ fontSize: 11.5, color: '#98A0AE' }}>{r.code}</Typography>
                                        </Box>
                                        <Typography sx={{ fontSize: 13 }}>{r.joined ? fmtApiDate(r.joined) : '—'}</Typography>
                                        <Typography sx={{ fontSize: 13 }}>{money(r.basic)}</Typography>
                                        <Typography sx={{ fontSize: 13 }}>{money(r.gross)}</Typography>
                                        <Tooltip arrow title={r.custom ? (r.fixedAmount ? 'Fixed amount set for this person' : 'Own percentage set for this person') : 'Follows the yearly percentage'}>
                                            <Chip
                                                label={r.pct != null ? `${Number(r.pct.toFixed(2))}%${r.custom ? ' ·' : ''}` : (r.fixedAmount ? 'Fixed' : '—')} size="small"
                                                sx={{ height: 22, fontSize: 11, fontWeight: 800, width: 'fit-content', bgcolor: r.custom ? '#FFF4E0' : PRIMARY_LIGHT, color: r.custom ? '#B7791F' : PRIMARY }}
                                            />
                                        </Tooltip>
                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#2F8F5F' }}>{r.amount != null && r.basic != null ? `+${inr(r.amount)}` : (r.fixedAmount ? `+${inr(r.amount)}` : '—')}</Typography>
                                        <Typography sx={{ fontSize: 13, fontWeight: 800 }}>{money(r.newBasic)}</Typography>
                                        <Typography sx={{ fontSize: 13, fontWeight: 800 }}>{money(r.newGross)}</Typography>
                                        <Stack direction="row" spacing={0.6} sx={{ alignItems: 'center' }}>
                                            <EventRepeatRoundedIcon sx={{ fontSize: 15, color: '#94A3B8' }} />
                                            <Typography sx={{ fontSize: 12.5, color: '#475569' }}>{r.due ? fmtLong(r.due) : '—'}</Typography>
                                        </Stack>
                                        {canEdit && (
                                            <Tooltip arrow title="Change this person's increment">
                                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); openOverride(r); }} sx={{ color: '#94A3B8', '&:hover': { color: PRIMARY, bgcolor: PRIMARY_LIGHT } }}>
                                                    <EditRoundedIcon sx={{ fontSize: 18 }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )
                ) : growthRows.length === 0 ? (
                    <EmptyState icon={TrendingUpRoundedIcon} title={q ? 'Nothing matches that search' : 'No growth increments yet'}
                        hint={canEdit ? 'Use Add growth increment to give someone an increment.' : 'Growth increments given to you will show here.'} />
                ) : (
                    <Box sx={{ overflowX: 'auto' }}>
                        <Box sx={{ minWidth: 700 }}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: GROW_COLS, gap: 2, px: 2.5, py: 1.2, bgcolor: '#FAFAFD', borderBottom: '1px solid #E6EAF1' }}>
                                {['EMPLOYEE', 'EFFECTIVE DATE', 'AMOUNT', 'REASON', ...(canEdit ? [''] : [])].map((h, i) => <Th key={`${h}${i}`}>{h}</Th>)}
                            </Box>
                            {growthRows.map((r, i) => (
                                <Box
                                    key={r.id ?? i} onClick={canEdit ? () => openEdit(r) : undefined}
                                    sx={{ display: 'grid', gridTemplateColumns: GROW_COLS, gap: 2, px: 2.5, py: 1.4, alignItems: 'center', borderBottom: '1px solid #F1F2F7', ...(canEdit && { cursor: 'pointer', '&:hover': { bgcolor: '#FAFAFD' } }) }}
                                >
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#0F172A' }} noWrap>{r.name || '—'}</Typography>
                                        <Typography sx={{ fontSize: 11.5, color: '#98A0AE' }}>{r.code}</Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: 13 }}>{r.date ? fmtApiDate(r.date) : '—'}</Typography>
                                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#2F8F5F' }}>+{inr(r.amount)}</Typography>
                                    <Typography sx={{ fontSize: 12.5, color: '#64748B' }}>{r.reason || '—'}</Typography>
                                    {canEdit && (
                                        <Stack direction="row" spacing={0.4} sx={{ justifyContent: 'flex-end' }}>
                                            <Tooltip arrow title="Edit"><IconButton size="small" onClick={(e) => { e.stopPropagation(); openEdit(r); }} sx={{ color: '#94A3B8', '&:hover': { color: PRIMARY, bgcolor: PRIMARY_LIGHT } }}><EditRoundedIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                                            <Tooltip arrow title="Remove"><IconButton size="small" onClick={(e) => { e.stopPropagation(); setConfirm(r); }} sx={{ color: '#94A3B8', '&:hover': { color: '#E11D48', bgcolor: '#FEF2F2' } }}><DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                                        </Stack>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}
            </Box>

            {/* Regular: one person */}
            <Dialog open={Boolean(ovRow)} onClose={() => { if (!saving) setOvRow(null); }} maxWidth="sm" fullWidth slotProps={{ paper: { sx: { borderRadius: '14px' } } }}>
                <DialogContent sx={{ pt: 3 }}>
                    <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 0.6, gap: 1 }}>
                        <Typography sx={{ fontSize: 17, fontWeight: 800, color: '#0F172A' }}>{ovRow?.add ? 'Add employee' : 'Increment'}</Typography>
                    </Stack>
                    <Box sx={{ ...FORM_GRID, mt: 1.6 }}>
                        {ovRow?.add && (
                            <Full>
                                <SearchSelect
                                    label="Employee" value={ovRow.code} onChange={(e) => pickRegularEmployee(e.target.value)} searchPlaceholder="Search employees…" sx={field}
                                    options={[{ value: '', label: 'Select employee', muted: true }, ...regularPickOptions]}
                                />
                            </Full>
                        )}
                        {/* Who and what their salary is, once someone is picked. */}
                        <Full>{ovRow?.code && (
                            <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5, flexWrap: 'wrap', px: 1.5, py: 1, borderRadius: '9px', bgcolor: '#F8FAFC', border: '1px solid #EEF0F6' }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0F172A' }}>{ovRow.name}</Typography>
                                <Typography sx={{ fontSize: 12, color: '#98A0AE' }}>{ovRow.code}</Typography>
                                <Box sx={{ flex: 1 }} />
                                {ovRow.basic != null ? (
                                    <Typography sx={{ fontSize: 12.5, color: '#475569' }}>Basic <strong>{inr(ovRow.basic)}</strong> · Gross <strong>{inr(ovRow.gross)}</strong></Typography>
                                ) : (
                                    <Typography sx={{ fontSize: 12.5, color: '#98A0AE' }}>{salaryLocked ? 'Salary locked' : 'No salary structure yet'}</Typography>
                                )}
                            </Stack>
                        )}</Full>
                        <Full>{ovRow?.code && otherKindNote(growthInfo(ovRow.code), has('growth') ? { label: 'Open Growth', onClick: switchToGrowth } : null)}</Full>
                        <Full><Switch2 value={ov.mode} size={30} onChange={(m) => { setApplyAsk(false); setOv((o) => ({ ...o, mode: m })); }} options={[['percent', 'By percentage'], ['amount', 'By amount']]} /></Full>
                        <CurrentDateField value={ov.current} onChange={(val) => setOv((o) => ({ ...o, current: val }))} />
                        <TextField select label="Salary type" size="small" fullWidth value={ov.salaryType} onChange={(e) => { setApplyAsk(false); setOv((o) => ({ ...o, salaryType: e.target.value })); }} sx={field}>
                            {SALARY_TYPES.map(([k, label]) => <MenuItem key={k} value={k} sx={{ fontSize: 13.5 }}>{label}</MenuItem>)}
                        </TextField>
                        <TextField
                            label={ov.mode === 'amount' ? 'Increment amount (₹)' : 'Yearly percentage (%)'} type="number" size="small" fullWidth autoFocus
                            value={ov.value} onChange={(e) => { setApplyAsk(false); setOv((o) => ({ ...o, value: e.target.value })); }}
                            error={ov.value !== '' && !ovOk} helperText={ov.value !== '' && !ovOk ? (ov.mode === 'amount' ? 'Enter 0 or more' : 'Enter 0 to 100') : undefined}
                            slotProps={{ htmlInput: { min: 0, ...(ov.mode === 'percent' ? { max: 100, step: 0.5 } : {}) } }} sx={field}
                        />
                        <MonthField label="Effective month" value={ov.date} onChange={(val) => setOv((o) => ({ ...o, date: val }))} />
                        <ExtraFields v={ov} onChange={(k, val) => setOv((o) => ({ ...o, [k]: val }))} designations={designationNames} />
                        <Full>
                            <TextField label="Reason (optional)" size="small" fullWidth multiline minRows={2} value={ov.reason} onChange={(e) => setOv((o) => ({ ...o, reason: e.target.value }))} sx={field} />
                        </Full>
                        <Full>{ovRow?.s && ovRow.basic != null && ovPreviewAmount != null && ovOk && (
                            <Box sx={{ p: 1.4, borderRadius: '9px', bgcolor: '#F8FAFC', border: '1px solid #EEF0F6' }}>
                                <Typography sx={{ fontSize: 12.5, color: '#475569' }}>
                                    Basic <strong>{inr(ovRow.basic)}</strong> → <strong>{inr(raised(ovRow.s, ovPreviewAmount, ov.salaryType).basic)}</strong> (+{inr(ovPreviewAmount)} on {typeLabel(ov.salaryType)})
                                </Typography>
                                <Typography sx={{ fontSize: 12.5, color: '#475569' }}>
                                    Gross <strong>{inr(ovRow.gross)}</strong> → <strong>{inr(raised(ovRow.s, ovPreviewAmount, ov.salaryType).gross)}</strong>
                                </Typography>
                            </Box>
                        )}</Full>
                        <Full>{ovRow?.custom && (
                            <Button onClick={resetOverride} disabled={saving} sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12.5, color: '#64748B', p: 0, minWidth: 0, justifyContent: 'flex-start' }}>
                                {ovRow.isNew ? 'Remove from Regular' : `Use the yearly ${regPercent}% again`}
                            </Button>
                        )}</Full>
                        {/* Switch at the bottom: turning Growth on moves this person to the Growth form. */}
                        <Full>{has('growth') && (
                            <Box sx={{ pt: 1, borderTop: '1px solid #EEF0F6' }}>
                                <FormControlLabel
                                    sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 13, fontWeight: 600, color: '#475569' } }}
                                    control={<Switch checked={false} onChange={() => switchToGrowth()} />}
                                    label="Growth increment"
                                />
                            </Box>
                        )}</Full>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, flexWrap: 'wrap', gap: 1 }}>
                    <Button onClick={() => setOvRow(null)} disabled={saving} sx={{ ...ghostBtn, height: 40, px: 2 }}>Cancel</Button>
                    <Box sx={{ flex: 1 }} />
                    <Button
                        onClick={() => (applyAsk ? applyOverride() : setApplyAsk(true))}
                        disabled={saving || !ovOk || ovPreviewAmount == null || !ovRow?.s || ovRow?.basic == null}
                        sx={{ ...tonalBtn, height: 40, px: 2, ...(applyAsk && { bgcolor: '#FFF4E0', color: '#B7791F', borderColor: '#F3D9A4' }) }}
                    >
                        {applyAsk ? 'Confirm: raise salary now' : 'Apply to salary'}
                    </Button>
                    <Button onClick={saveOverride} disabled={saving || !ovOk} startIcon={saving ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : null} sx={{ ...solidBtn, height: 40, px: 2.4, '&.Mui-disabled': { opacity: 0.5, color: '#fff', bgcolor: PRIMARY } }}>
                        {saving ? 'Saving…' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Growth: add / edit */}
            <Dialog open={Boolean(dialog)} onClose={() => { if (!saving) setDialog(null); }} maxWidth="sm" fullWidth slotProps={{ paper: { sx: { borderRadius: '14px' } } }}>
                <DialogContent sx={{ pt: 3 }}>
                    <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 1 }}>
                        <Typography sx={{ fontSize: 17, fontWeight: 800, color: '#0F172A' }}>{dialog === 'edit' ? 'Edit growth increment' : 'Growth increment'}</Typography>
                    </Stack>
                    {formError && <Alert severity="error" sx={{ mb: 1.6, borderRadius: '9px', fontSize: 12.5, py: 0.4 }}>{formError}</Alert>}
                    <Box sx={FORM_GRID}>
                        <Full>
                            <SearchSelect
                                label="Employee" value={form.employeeCode} onChange={(e) => pickGrowthEmployee(e.target.value)}
                                disabled={dialog === 'edit'} error={tried && !form.employeeCode}
                                searchPlaceholder="Search employees…" sx={field}
                                options={[{ value: '', label: 'Select employee', muted: true }, ...growthPickOptions]}
                            />
                        </Full>
                        {/* Everyone active is in the Regular structure (on the yearly %, or their own), so tell the admin and offer to go there first. */}
                        <Full>{form.employeeCode && regularEveryone.some((r) => r.code === form.employeeCode) && otherKindNote(
                            addableCodes.has(form.employeeCode)
                                ? { icon: EventRepeatRoundedIcon, title: 'Not in the Regular structure yet', detail: 'Newly added. Add them to Regular if they also get a yearly increment.' }
                                : regularInfo(form.employeeCode),
                            has('regular') && dialog === 'add' ? { label: addableCodes.has(form.employeeCode) ? 'Add to Regular' : 'Open Regular', onClick: switchToRegular } : null,
                        )}</Full>
                        <Full><Switch2 value={form.mode} size={30} onChange={(m) => setForm((f) => ({ ...f, mode: m }))} options={[['percent', 'By percentage'], ['amount', 'By amount']]} /></Full>
                        <CurrentDateField value={form.current} onChange={(val) => setForm((f) => ({ ...f, current: val }))} />
                        <TextField select label="Salary type" size="small" fullWidth value={form.salaryType} onChange={set('salaryType')} sx={field}>
                            {SALARY_TYPES.map(([k, label]) => <MenuItem key={k} value={k} sx={{ fontSize: 13.5 }}>{label}</MenuItem>)}
                        </TextField>
                        {form.mode === 'percent' ? (
                            <TextField label="Increment percentage (%)" type="number" size="small" fullWidth value={form.percent} onChange={set('percent')}
                                error={tried && !(amountNow > 0)}
                                helperText={tried && !(amountNow > 0) ? (formBase == null ? 'Needs a salary structure to work out' : 'Enter a percentage above 0') : undefined}
                                slotProps={{ htmlInput: { min: 0, max: 100, step: 0.5 } }} sx={field} />
                        ) : (
                            <TextField label="Increment amount (₹)" type="number" size="small" fullWidth value={form.amount} onChange={set('amount')}
                                error={tried && !(amountNow > 0)} helperText={tried && !(amountNow > 0) ? 'Enter an amount above 0' : undefined}
                                slotProps={{ htmlInput: { min: 0 } }} sx={field} />
                        )}
                        <MonthField label="Effective month" value={form.date} error={tried && !form.date} onChange={(val) => setForm((f) => ({ ...f, date: val }))} />
                        <ExtraFields v={form} onChange={(k, val) => setForm((f) => ({ ...f, [k]: val }))} designations={designationNames} />
                        <Full>
                            <TextField label="Reason (optional)" size="small" fullWidth multiline minRows={2} value={form.reason} onChange={set('reason')} sx={field} />
                        </Full>
                        <Full>{form.mode === 'percent' && amountNow > 0 && (
                            <Typography sx={{ fontSize: 12.5, color: '#64748B' }}>That works out to <strong>{inr(amountNow)}</strong> on {typeLabel(form.salaryType)} salary.</Typography>
                        )}</Full>
                        <Full>{dialog === 'add' && form.employeeCode && (
                            canRaise ? (
                                <Box>
                                    <FormControlLabel
                                        sx={{ m: 0, alignItems: 'flex-start', '& .MuiFormControlLabel-label': { fontSize: 12.5, color: '#475569' } }}
                                        control={<Checkbox size="small" checked={form.alsoRaise} onChange={(e) => setForm((f) => ({ ...f, alsoRaise: e.target.checked }))} sx={{ p: 0.5, mr: 0.6, color: '#C9BEFB', '&.Mui-checked': { color: PRIMARY } }} />}
                                        label={`Also raise this person's ${typeLabel(form.salaryType)} salary by this amount`}
                                    />
                                    {form.alsoRaise && amountNow > 0 && (
                                        <Typography sx={{ fontSize: 12, color: '#64748B', ml: 3.4 }}>
                                            Basic {inr(figures(formStructure).basic)} → {inr(raised(formStructure, amountNow, form.salaryType).basic)} · gross {inr(figures(formStructure).gross)} → {inr(raised(formStructure, amountNow, form.salaryType).gross)}
                                        </Typography>
                                    )}
                                </Box>
                            ) : (
                                <Typography sx={{ fontSize: 12, color: '#98A0AE' }}>
                                    {salaryLocked ? 'Salary figures are locked, so the salary structure can’t be updated from here.' : 'No salary structure for this person yet, so only the record is saved.'}
                                </Typography>
                            )
                        )}</Full>
                        {/* Switch at the bottom: turning Growth off moves this person to the Regular form. */}
                        <Full>{dialog === 'add' && has('regular') && (
                            <Box sx={{ pt: 1, borderTop: '1px solid #EEF0F6' }}>
                                <FormControlLabel
                                    sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 13, fontWeight: 600, color: '#475569' } }}
                                    control={<Switch checked onChange={() => switchToRegular()} />}
                                    label="Growth increment"
                                />
                            </Box>
                        )}</Full>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setDialog(null)} disabled={saving} sx={{ ...ghostBtn, height: 40, px: 2 }}>Cancel</Button>
                    <Button onClick={save} disabled={saving} startIcon={saving ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : null} sx={{ ...solidBtn, height: 40, px: 2.4 }}>
                        {saving ? 'Saving…' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={Boolean(confirm)} onClose={() => { if (!saving) setConfirm(null); }} onConfirm={remove}
                title="Remove this growth increment?"
                body={confirm ? `${confirm.name}'s growth increment of ${inr(confirm.amount)} will be removed. Their salary structure is not changed.` : ''}
                confirmLabel="Remove increment"
            />

            <SecretDialog
                open={unlockOpen} title="Encryption Password" subtitle="Needed to see and change salary figures."
                fields={[{ key: 'pass', label: 'Encryption Password' }]} submitLabel="Unlock"
                busy={unlockBusy} error={unlockError} onClose={() => setUnlockOpen(false)} onSubmit={submitUnlock}
            />

            <Snackbar open={Boolean(snack)} autoHideDuration={5000} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                {snack ? <Alert severity={snack.sev} onClose={() => setSnack(null)} sx={{ borderRadius: '9px' }}>{snack.msg}</Alert> : undefined}
            </Snackbar>
        </Box>
    );
}
